import { CurrencyPipe, PercentPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, EventEmitter, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseComponent, Grid, ListItem } from '../../../shared'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { GridExportComponent } from '../../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'

interface OverheadLaborLine {
    id: string
    label: string
    cost: number
    percent: number | null
}

interface OverheadAccount {
    id: string
    category: string
    account: string
    description: string
    amount: number
}

interface OverheadMonth {
    month: string
    label: string
    year: number
    monthNumber: number
    revenue: number
    laborPercent: number | null
    laborRelatedPercent: number | null
    otherPercent: number | null
    estimatingPercent: number | null
    totalCost: number
    totalPercent: number | null
    accounts: OverheadAccount[]
}

interface OverheadYear {
    year: number
    label: string
    isYearToDate: boolean
    revenue: number
    laborPercent: number | null
    laborRelatedPercent: number | null
    otherPercent: number | null
    estimatingPercent: number | null
    totalCost: number
    totalPercent: number | null
    laborLines: OverheadLaborLine[]
    months: OverheadMonth[]
}

interface ChartSeries {
    binding: string
    name: string
}

@Component({
    selector: 'overhead-rates',
    templateUrl: './overhead-rates.component.html',
    standalone: true,
    imports: [
        FormsModule,
        CurrencyPipe,
        PercentPipe,
        WjInputModule,
        WjChartModule,
        WjGridModule,
        WjGridFilterModule,
        ActionToolbarCount,
        GridExportComponent,
        GridSelectionCalculationComponent,
    ],
})
export class OverheadRatesComponent extends BaseComponent {
    @Output() unmappedCountChange = new EventEmitter<number>()

    readonly currentYear = new Date().getFullYear()
    startYear = this.currentYear - 5
    endYear = this.currentYear
    yearOptions: ListItem<number>[] = []
    years: number[] = []
    rows: OverheadYear[] = []
    monthRows: OverheadMonth[] = []
    selectedYear: OverheadYear | null = null
    selectedMonth: OverheadMonth | null = null
    closedThrough = ''
    rangeInvalid = false
    loadError = ''
    chartPoints: Record<string, string | number | null>[] = []
    chartSeries: ChartSeries[] = [
        { binding: 'total', name: 'Total' },
        { binding: 'labor', name: 'Labor' },
        { binding: 'laborRelated', name: 'Labor Related' },
        { binding: 'other', name: 'Other' },
        { binding: 'estimating', name: 'Estimating' },
    ]
    unmappedCount = 0

    yearGrid: Grid<OverheadYear>
    laborGrid: Grid<OverheadLaborLine>
    monthGrid: Grid<OverheadMonth>
    accountGrid: Grid<OverheadAccount>
    validationGrid: Grid<any>

    constructor() {
        super()
        this.yearOptions = this.buildYearOptions()
        this.yearGrid = new Grid<OverheadYear>(this.services, 'year', this).wrapColumnHeaders()
        this.laborGrid = new Grid<OverheadLaborLine>(this.services, 'id', this).wrapColumnHeaders()
        this.monthGrid = new Grid<OverheadMonth>(this.services, 'month', this).wrapColumnHeaders()
        this.accountGrid = new Grid<OverheadAccount>(this.services, 'id', this).wrapColumnHeaders()
        this.validationGrid = new Grid<any>(this.services, 'account', this).wrapColumnHeaders()
        this.yearGrid.selectionChanged.on(row => this.showYear(row))
        this.monthGrid.selectionChanged.on(row => this.showMonth(row))
        this.load()
    }

    onRangeChanged() {
        this.rangeInvalid = this.startYear > this.endYear
        if (this.rangeInvalid) return
        this.load()
    }

    load() {
        this.loadError = ''
        const params = new HttpParams().set('startYear', this.startYear).set('endYear', this.endYear)
        this.cancelableRequest(
            'overheadRates',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/BidRate/GetOverheadRates', params)).subscribe({
                next: result => {
                    const payload = result[0] as {
                        years: number[]
                        rows: any[]
                        unmappedAccounts: any[]
                        closedThrough: string
                    }
                    this.closedThrough = payload.closedThrough ?? ''
                    this.years = payload.years ?? []
                    this.rows = (payload.rows ?? []).map(row => this.mapYear(row))
                    this.chartPoints = [...this.rows].reverse().map(row => ({
                        year: row.label,
                        total: row.totalPercent,
                        labor: row.laborPercent,
                        laborRelated: row.laborRelatedPercent,
                        other: row.otherPercent,
                        estimating: row.estimatingPercent,
                    }))
                    this.yearGrid.items = this.rows
                    this.showYear(this.rows[0] ?? null)

                    const unmapped = (payload.unmappedAccounts ?? []).map(row => {
                        const mapped: Record<string, string | number | null> = {
                            account: row.account,
                            description: row.description,
                            total: row.total,
                        }
                        for (const amount of row.amounts ?? []) mapped['amount' + amount.year] = amount.amount
                        return mapped
                    })
                    this.unmappedCount = unmapped.length
                    this.validationGrid.items = unmapped
                    this.unmappedCountChange.emit(this.unmappedCount)
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.loadError = 'Overhead rates could not be loaded.'
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    get latestYear(): OverheadYear | undefined {
        return this.rows[0]
    }

    get priorYear(): OverheadYear | undefined {
        return this.rows[1]
    }

    get closedLabel(): string {
        return this.monthLabel(this.closedThrough)
    }

    private showYear(row: OverheadYear | null) {
        this.selectedYear = row
        this.laborGrid.items = row?.laborLines ?? []
        this.monthRows = row ? [...row.months].reverse() : []
        this.monthGrid.items = this.monthRows
        this.showMonth(this.monthRows[0] ?? null)
    }

    private showMonth(row: OverheadMonth | null) {
        this.selectedMonth = row
        this.accountGrid.items = row?.accounts ?? []
    }

    private mapYear(row: any): OverheadYear {
        return {
            year: row.year,
            label: row.isYearToDate ? `${row.year} YTD` : String(row.year),
            isYearToDate: row.isYearToDate,
            revenue: row.revenue,
            laborPercent: row.laborPercent,
            laborRelatedPercent: row.laborRelatedPercent,
            otherPercent: row.otherPercent,
            estimatingPercent: row.estimatingPercent,
            totalCost: row.totalCost,
            totalPercent: row.totalPercent,
            laborLines: (row.laborLines ?? []).map((line: any) => ({
                id: line.key,
                label: line.label,
                cost: line.cost,
                percent: line.percent,
            })),
            months: (row.months ?? []).map((month: any) => ({
                month: month.month,
                label: this.monthLabel(month.month),
                year: month.year,
                monthNumber: month.monthNumber,
                revenue: month.revenue,
                laborPercent: month.laborPercent,
                laborRelatedPercent: month.laborRelatedPercent,
                otherPercent: month.otherPercent,
                estimatingPercent: month.estimatingPercent,
                totalCost: month.totalCost,
                totalPercent: month.totalPercent,
                accounts: (month.accounts ?? []).map((account: OverheadAccount) => ({
                    ...account,
                    id: `${account.category}|${account.account}`,
                })),
            })),
        }
    }

    private buildYearOptions(): ListItem<number>[] {
        const options: ListItem<number>[] = []
        for (let year = this.currentYear; year >= 2012; year--) options.push(new ListItem(year, String(year)))
        return options
    }

    private monthLabel(value: string | Date | null | undefined): string {
        const date = this.toMonthDate(value)
        if (!date) return ''
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }

    private toMonthDate(value: string | Date | null | undefined): Date | null {
        if (!value) return null
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), 1)
        }
        if (value.length < 7) return null
        const [year, month] = value.substring(0, 7).split('-').map(part => Number(part))
        if (!year || !month) return null
        return new Date(year, month - 1, 1)
    }
}
